// src/features/rooms/hooks/useRoomFiles.js
import { useState, useCallback } from 'react';
import { useFiles } from '../../files/hooks/useFiles';
import { useFolders } from '../../files/hooks/useFolders';

const ROOMS_FOLDER_NAME = 'i18n-files:i18n_folder_names.rooms';

export function useRoomFiles() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  
  const { uploadFile } = useFiles();
  const { getRootContents, createFolder, getFolderContents } = useFolders();

  /**
   * Find or create the "Rooms" meta folder. With `{ create: false }` (just
   * browsing) nothing is created and a missing folder gives null.
   * @returns {Promise<string|null>} folder_id of the Rooms folder
   */
  const ensureRoomsMetaFolder = useCallback(async ({ create = true } = {}) => {
    try {
      // Get root contents
      const rootResult = await getRootContents();
      
      if (!rootResult?.success) {
        throw new Error('Failed to fetch root contents');
      }

      const rootData = rootResult.data.data;
      
      // Look for existing "Rooms" folder
      const existingFolder = rootData.folders?.find(
        folder => folder.folder_name === ROOMS_FOLDER_NAME
      );

      if (existingFolder) {
        return existingFolder.folder_id;
      }
      if (!create) return null;

      // Create the Rooms meta folder if it doesn't exist
      const createResult = await createFolder({
        folder_name: ROOMS_FOLDER_NAME,
        description: 'System folder for room files organized by room name',
        parent_folder_id: null, // Root level
        is_system_folder: false,
        is_protected: false,
      });

      if (!createResult?.success) {
        throw new Error('Failed to create Rooms folder');
      }

      return createResult.data.data.folder_id;
    } catch (err) {
      console.error('Error ensuring Rooms meta folder:', err);
      throw err;
    }
  }, [getRootContents, createFolder]);

  /**
   * Find or create a specific room's folder within the Rooms meta folder.
   * With `{ create: false }` nothing is created and a missing folder gives null,
   * so just viewing a room doesn't leave an empty folder behind.
   * @param {string} roomName - The name of the room
   * @returns {Promise<string|null>} folder_id of the room's folder
   */
  const ensureRoomFolder = useCallback(async (roomName, { create = true } = {}) => {
    try {
      // First ensure the Rooms meta folder exists
      const roomsMetaFolderId = await ensureRoomsMetaFolder({ create });
      if (!roomsMetaFolderId) return null;

      // Get contents of the Rooms meta folder
      const contentsResult = await getFolderContents(roomsMetaFolderId);
      
      if (!contentsResult?.success) {
        throw new Error('Failed to fetch Rooms folder contents');
      }

      const folderContents = contentsResult.data.data;
      
      // Look for existing room folder with this name
      const existingRoomFolder = folderContents.subfolders?.find(
        folder => folder.folder_name === roomName
      );

      if (existingRoomFolder) {
        return existingRoomFolder.folder_id;
      }
      if (!create) return null;

      // Create the room-specific folder if it doesn't exist
      const createResult = await createFolder({
        folder_name: roomName,
        description: `Files for room: ${roomName}`,
        parent_folder_id: roomsMetaFolderId,
        is_system_folder: false,
        is_protected: false,
      });

      if (!createResult?.success) {
        throw new Error('Failed to create room folder');
      }

      return createResult.data.data.folder_id;
    } catch (err) {
      console.error('Error ensuring room folder:', err);
      throw err;
    }
  }, [ensureRoomsMetaFolder, getFolderContents, createFolder]);

  /**
   * Upload a file to a room's folder
   * @param {File} file - The file to upload
   * @param {string} roomName - The room's name (for folder structure)
   * @param {Object} options.onUploadProgress - Upload progress events, for a progress bar
   * @returns {Promise<Object>} Upload result
   */
  const uploadRoomFile = useCallback(async (file, roomName, { onUploadProgress } = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure the room's folder exists in the structure: Rooms/RoomName
      const roomFolderId = await ensureRoomFolder(roomName);

      // Upload the file to the room's folder
      const uploadResult = await uploadFile(file, {
        folder_id: roomFolderId,
        is_system_file: false,
        onUploadProgress,
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || 'File upload failed');
      }

      setLoading(false);
      return {
        success: true,
        data: uploadResult.data,
        folderId: roomFolderId,
      };
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [uploadFile, ensureRoomFolder]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear files state
   */
  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  return {
    loading,
    error,
    files,
    // Actions
    uploadRoomFile,
    ensureRoomFolder,
    ensureRoomsMetaFolder,
    clearError,
    clearFiles,
  };
}